<template>
  <div class="flex flex-col relative rounded-t-3xl h-[85vh] overflow-auto">
    <!-- Header Section -->
    <div class="">
      <!-- Title Section -->
      <div
        class="px-6 pt-6 pb-4 bg-gradient-to-b from-orange-50 to-white rounded-t-3xl"
      >
        <div class="mobile-status-padding"></div>
        <h1
          class="text-2xl font-bold text-slate-800 text-center tracking-tight"
        >
          Mon Portefeuille
        </h1>
        <p class="text-sm text-slate-500 text-center mt-1.5">
          Gérez vos fonds facilement
        </p>
      </div>
    </div>

    <!-- Divider -->
    <div class="py-2 border-t border-slate-100"></div>

    <!-- Balance Section -->
    <div class="px-6 py-0">
      <div
        class="bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl px-6 py-4 text-white shadow-lg shadow-orange-500/30"
      >
        <p class="text-orange-100 text-sm font-medium">Solde disponible</p>
        <p class="text-3xl font-bold mt-1 tracking-tight">
          {{ formatBalance(balance) }} XOF
        </p>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="px-6 py-3 flex gap-3">
      <button
        @click="handleDeposit"
        class="flex-1 bg-emerald-500 hover:bg-emerald-600 text-lg font-bold text-white py-3 rounded-xl transition-all duration-300 active:scale-95 shadow-lg shadow-emerald-500/25"
      >
        <span class="flex items-center justify-center gap-2">
          <ColorImg
            src="/images/all/topup.png"
            class="bg-white w-8 h-8"
            alt=""
          />
          Déposer
        </span>
      </button>
      <button
        @click="handleWithdraw"
        class="flex-1 bg-rose-500 hover:bg-rose-600 text-lg font-bold text-white py-3 rounded-xl transition-all duration-300 active:scale-95 shadow-lg shadow-rose-500/25"
      >
        <span class="flex items-center justify-center gap-2">
          <ColorImg
            src="/images/all/deposit.png"
            class="bg-white w-8 h-8"
            alt=""
          />
          Retirer
        </span>
      </button>
    </div>

    <!-- Transaction History Section -->
    <div class="px-6 py-1 overflow-hidden flex flex-col h-full overflow-auto">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-lg font-bold text-slate-800">Historique</h2>
        <button
          v-if="!loading && transactions.length > 0"
          @click="refreshTransactions"
          class="text-orange-500 text-sm font-medium hover:text-orange-600 active:scale-95 transition-all"
        >
          Actualiser
        </button>
      </div>

      <!-- Filter Tabs -->
      <div
        class="bg-slate-100/80 rounded-2xl p-1.5 flex gap-1.5 shadow-inner mb-5 overflow-auto"
      >
        <button
          v-for="filter in transactionFilters"
          :key="filter.key"
          @click="selectedFilter = filter.key"
          class="flex-1 flex items-center justify-center py-2.5 px-3 rounded-xl text-sm font-semibold transition-all duration-300 whitespace-nowrap"
          :class="[
            selectedFilter === filter.key
              ? 'bg-white text-orange-500 shadow-lg shadow-orange-500/10'
              : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
          ]"
        >
          {{ filter.label }}
        </button>
      </div>

      <!-- Transactions List -->
      <div
        v-if="!loading && filteredTransactions.length > 0"
        class="space-y-3 h-full overflow-auto"
      >
        <div
          v-for="transaction in filteredTransactions"
          :key="transaction.id"
          class="bg-white rounded-2xl p-4 border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer group"
          @click="handleTransactionClick(transaction)"
        >
          <div class="flex items-start gap-3">
            <!-- Icon -->
            <div
              class="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:scale-105 shrink-0 mt-0.5"
              :class="getTransactionIconBg(transaction)"
            >
              <ColorImg
                src="/images/all/topup.png"
                class="bg-current w-6 h-6"
                alt=""
                v-if="transaction.type === 'credit'"
              />
              <ColorImg
                src="/images/all/deposit.png"
                class="bg-current w-6 h-6"
                alt=""
                v-else
              />
            </div>

            <!-- Info -->
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-2">
                <p class="font-bold text-slate-800 text-base">
                  {{ transaction.type === 'credit' ? '+' : '-' }}{{ formatBalance(transaction.amount) }} XOF
                </p>

                <div class="flex items-center gap-2 shrink-0">
                  <span
                    class="text-xs font-semibold px-3 py-1 rounded-full transition-all duration-300"
                    :class="getStatusClass(transaction.status)"
                  >
                    {{ getStatusLabel(transaction.status) }}
                  </span>

                  <!-- Chevron d'extension -->
                  <svg
                    v-if="transaction.fees"
                    class="w-4 h-4 text-slate-400 transition-transform duration-300"
                    :class="{ 'rotate-180': expandedTransactionId === transaction.id }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>

              <!-- Accordéon / Dropdown Détails des Frais -->
              <transition
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 max-h-0 overflow-hidden"
                enter-to-class="opacity-100 max-h-40 overflow-hidden"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 max-h-40 overflow-hidden"
                leave-to-class="opacity-0 max-h-0 overflow-hidden"
              >
                <div
                  v-if="transaction.fees && expandedTransactionId === transaction.id"
                  class="text-xs mt-2 space-y-1 bg-slate-50 p-2.5 rounded-xl border border-slate-100"
                  @click.stop
                >
                  <div class="flex justify-between text-slate-700 font-medium">
                    <span>Retiré :</span>
                    <span>{{ formatBalance(transaction.fees.srcAmount) }} XOF</span>
                  </div>
                  <div class="flex justify-between text-emerald-600 font-medium">
                    <span>Déposé :</span>
                    <span>{{ formatBalance(transaction.fees.dstAmount) }} XOF</span>
                  </div>
                  <div class="flex justify-between text-rose-500 font-medium">
                    <span>Frais :</span>
                    <span>{{ formatBalance(transaction.fees.totalFeeCalculated) }} XOF</span>
                  </div>
                </div>
              </transition>

              <div class="flex items-center justify-between mt-2 pt-1 border-t border-slate-50">
                <p class="text-xs text-slate-500 flex items-center gap-1.5">
                  <svg
                    class="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  {{ formatDate(transaction.createdAt) }}
                </p>
                <p class="text-xs text-slate-400 font-medium">
                  {{ transaction.phoneNumber || getPaymentMethodLabel(transaction.paymentMethod) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!loading" class="text-center py-10">
        <div
          class="inline-flex items-center justify-center w-20 h-20 bg-slate-100 rounded-full mb-4"
        >
          <svg
            class="w-10 h-10 text-slate-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
        </div>
        <p class="font-semibold text-slate-600">Aucune transaction</p>
        <p class="text-sm text-slate-400 mt-1">
          {{
            selectedFilter === 'all'
              ? "Vous n'avez aucune transaction pour le moment."
              : getEmptyMessageForFilter(selectedFilter)
          }}
        </p>
      </div>

      <!-- Loading State -->
      <div v-else class="text-center py-10">
        <div
          class="inline-flex items-center justify-center w-16 h-16 bg-orange-100 rounded-full mb-4 animate-pulse"
        >
          <svg
            class="w-8 h-8 text-orange-500 animate-spin"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        </div>
        <p class="text-slate-500 font-medium">Chargement...</p>
      </div>
    </div>

    <!-- Divider -->
    <div class="py-2 border-t border-slate-100"></div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import constants from '@core/utils/constants';

const emit = defineEmits(['close']);

// Store
const walletStore = getStore('wallet');
const dialogStore = getStore('dialog');

// State
const balance = ref(0);
const transactions = ref([]);
const loading = ref(true);
const selectedFilter = ref('all');
const expandedTransactionId = ref(null); // ID de la transaction actuellement dépliée

// Transaction filters
const transactionFilters = [
  { key: 'all', label: 'Toutes' },
  { key: 'credit', label: 'Dépôts' },
  { key: 'debit', label: 'Retraits' },
  { key: 'completed', label: 'Réussies' },
  { key: 'pending', label: 'En cours' },
  { key: 'failed', label: 'Échouées' }
];

// Computed
const filteredTransactions = computed(() => {
  let filtered = [...transactions.value];

  switch (selectedFilter.value) {
    case 'credit':
      return filtered.filter((t) => t.type === 'credit');
    case 'debit':
      return filtered.filter((t) => t.type === 'debit');
    case 'completed':
      return filtered.filter((t) => t.status === 'completed');
    case 'pending':
      return filtered.filter((t) => t.status === 'pending');
    case 'failed':
      return filtered.filter((t) => t.status === 'failed');
    default:
      return filtered;
  }
});

// Methods
function formatBalance(amount) {
  return new Intl.NumberFormat('fr-FR').format(amount || 0);
}

function formatDate(date) {
  if (!date) return '';
  const d = new Date(date);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);

  if (diff < 60) return "À l'instant";
  if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `Il y a ${Math.floor(diff / 86400)}j`;

  return d.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  });
}

function getStatusLabel(status) {
  const labels = {
    completed: 'Réussie',
    pending: 'En cours',
    failed: 'Échouée'
  };
  return labels[status] || status;
}

function getStatusClass(status) {
  const classes = {
    completed: 'bg-emerald-100 text-emerald-700',
    pending: 'bg-primary-100 text-primary-700',
    failed: 'bg-rose-100 text-rose-700'
  };
  return classes[status] || 'bg-slate-100 text-slate-700';
}

function getTransactionIconBg(transaction) {
  if (transaction.status === 'completed') {
    return transaction.type === 'credit'
      ? 'bg-emerald-100 text-emerald-600'
      : 'bg-rose-100 text-rose-600';
  } else if (transaction.status === 'pending') {
    return 'bg-primary-100 text-primary-600';
  } else {
    return 'bg-slate-100 text-slate-500';
  }
}

function getPaymentMethodLabel(method) {
  const labels = {
    moov: 'Moov',
    mtn: 'MTN',
    card: 'Carte',
    wallet: 'Wallet'
  };
  return labels[method] || method || 'Wallet';
}

function getEmptyMessageForFilter(filter) {
  const messages = {
    credit: 'Aucun dépôt pour le moment.',
    debit: 'Aucun retrait pour le moment.',
    completed: 'Aucune transaction réussie.',
    pending: 'Aucune transaction en cours.',
    failed: 'Aucune transaction échouée.'
  };
  return messages[filter] || 'Aucune transaction.';
}

function handleDeposit() {
  bus.emit(constants.EVENTS.WALLET.DEPOSIT_OPEN);
}

function handleWithdraw() {
  bus.emit(constants.EVENTS.WALLET.WITHDRAWAL_OPEN);
}

function handleTransactionClick(transaction) {
  // Déplie / Replie le composant si la transaction a des frais
  if (transaction.fees) {
    if (expandedTransactionId.value === transaction.id) {
      expandedTransactionId.value = null;
    } else {
      expandedTransactionId.value = transaction.id;
    }
  }

  // Émet l'événement global habituel
  bus.emit(constants.EVENTS.WALLET.TRANSACTION_DETAIL, {
    transactionId: transaction.id,
    timestamp: Date.now()
  });
}

function refreshTransactions() {
  loading.value = true;
  bus.emit(constants.EVENTS.WALLET.TRANSACTIONS_LOAD, {
    page: 1,
    limit: 20
  });
}

// Load wallet data
function loadWalletData() {
  bus.emit(constants.EVENTS.WALLET.BALANCE_LOAD);
  bus.emit(constants.EVENTS.WALLET.TRANSACTIONS_LOAD, {
    page: 1,
    limit: 20
  });
}

// Listen for wallet updates
function onBalanceUpdate(data) {
  balance.value = data?.balance || 0;
}

function onTransactionsUpdate(data) {
  transactions.value = data?.transactions || [];
  loading.value = false;
}

function onBalanceError() {
  loading.value = false;
}

let pollTimer = null;

// Lifecycle
onMounted(() => {
  loadWalletData();

  pollTimer = setInterval(() => {
    loadWalletData();
  }, 5000);

  bus.on(constants.EVENTS.WALLET.BALANCE_UPDATE, onBalanceUpdate);
  bus.on(constants.EVENTS.WALLET.TRANSACTIONS_UPDATE, onTransactionsUpdate);
  bus.on(constants.EVENTS.WALLET.BALANCE_ERROR, onBalanceError);
});

onUnmounted(() => {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  bus.off(constants.EVENTS.WALLET.BALANCE_UPDATE, onBalanceUpdate);
  bus.off(constants.EVENTS.WALLET.TRANSACTIONS_UPDATE, onTransactionsUpdate);
  bus.off(constants.EVENTS.WALLET.BALANCE_ERROR, onBalanceError);
});
</script>